/**
 * Logs panel for the workspace.
 *
 * Surfaces runtime logs from the project's dev or prod container. The live SSE
 * stream (`GET /projects/:id/logs/stream`) is ALWAYS on — it's primed with a
 * tail and appends new lines as they arrive (no "Live" toggle). Two selectors
 * scope what's shown: the dev/prod source toggle, and (dev only) a service
 * selector — the dev sandbox runs the backend (`api`) and the Vite dev server
 * (`app`) as separate processes, so you can watch all, just the API, or just the
 * app. Output is monospace and auto-scrolls to the newest line, but pauses
 * auto-scroll the moment you scroll up to read and resumes once you return to
 * the bottom (a "Jump to latest" button appears while paused). Each line is
 * color-coded by detected level (error/warn/info/debug) using theme tokens.
 *
 * Pure log shaping (line splitting, capped append + reconnect dedup, per-line
 * level classification) and the at-bottom check live in
 * `utilities/ops-utilities.ts` so they're unit-tested without a DOM.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex } from '@molecule/app-ui-react'

import { silentErrors } from '../bonds/app-http.js'
import { API_BASE_URL } from '../config.js'
import { useIsMobile } from '../hooks/useMediaQuery.js'
import {
  appendLogDelta,
  classifyLogLine,
  DEFAULT_LOG_LINE_CAP,
  EMPTY_STATE_ERROR_KEYS,
  isScrolledToBottom,
  type LogLevel,
  logsEmptyStateKey,
  type LogService,
  logsRequest,
  type LogsResponse,
  logsStreamPath,
  type LogStreamEvent,
  type OpsEnv,
  splitLogLines,
} from '../utilities/ops-utilities.js'
import { Icon } from './Icon.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'
import { usePanelModalTabs } from './PanelModal.js'

/** Props for the Logs panel. */
interface LogsPanelProps {
  /** Project whose runtime logs to read/stream. */
  projectId: string
}

/** Lines to request on a tail / prime the live stream with. */
const TAIL_LINES = 500

/**
 * Reconnect attempts before the panel STOPS retrying and says so.
 *
 * A stream that keeps failing is a degraded dependency, and reconnecting into it
 * forever aims a retry storm at exactly the thing that is already struggling —
 * per open panel, per user. After this many the panel gives up and offers a
 * Retry button, so recovery is one deliberate click rather than an unattended
 * loop.
 */
const MAX_STREAM_ATTEMPTS = 5

/**
 * Per-line color coding by detected level — ALL theme css-var tokens (no
 * hardcoded hex, enforced by the logs-panel-classmap guard), so the palette
 * tracks the user's theme in light + dark. `log` keeps the original neutral
 * viewport color so an unclassified line reads exactly as before.
 */
const LOG_LEVEL_COLOR: Record<LogLevel, string> = {
  error: 'var(--color-error)',
  warn: 'var(--color-warning)',
  info: 'var(--color-info)',
  debug: 'var(--color-foreground-tertiary)',
  log: 'var(--color-foreground-secondary)',
}

/** Shared control height for the header toolbar — matches the all-modals 32px. */
const CONTROL_HEIGHT = 32

/** Touch-floor control height for the header toolbar on phones. */
const CONTROL_HEIGHT_MOBILE = 40

/**
 * Logs panel — dev/prod source + (dev) service selectors, an always-on live
 * tail, capped line buffer, and scroll-aware auto-scroll.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose logs to read.
 * @returns The rendered Logs panel.
 */
export function LogsPanel({ projectId }: LogsPanelProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const isMobile = useIsMobile()
  // Phones get a real touch target for the toolbar controls; desktop keeps
  // the compact all-modals 32px.
  const controlHeight = isMobile ? CONTROL_HEIGHT_MOBILE : CONTROL_HEIGHT

  const [source, setSource] = useState<OpsEnv>('dev')
  const [service, setService] = useState<LogService>('all')

  // Dev/Prod source tabs render in the MODAL HEADER as the shared segmented
  // control (usePanelModalTabs) — one tab idiom across every workspace modal.
  // The service selector below stays in the body: it filters WITHIN dev.
  usePanelModalTabs({
    tabs: [
      {
        id: 'dev',
        label: t('logs.source.dev', undefined, { defaultValue: 'Dev' }),
        molId: 'logs-source-dev',
      },
      {
        id: 'prod',
        label: t('logs.source.prod', undefined, { defaultValue: 'Prod' }),
        molId: 'logs-source-prod',
      },
    ],
    activeId: source,
    onSelect: (id) => setSource(id as OpsEnv),
    label: t('logs.source.label', undefined, { defaultValue: 'Log source' }),
    molId: 'logs-source-toggle',
  })
  const [lines, setLines] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [containerId, setContainerId] = useState<string | null>(null)
  // WHICH expected-empty state we are in, so the copy can name it: "nothing
  // deployed yet" and "the sandbox is asleep" have different next steps.
  const [emptyKey, setEmptyKey] = useState<string | null>(null)
  // Re-opens the live stream after the panel gave up retrying. Registered by the
  // stream effect below so Retry re-subscribes in place, without tearing the
  // effect down and rebuilding it.
  const restartStreamRef = useRef<(() => void) | null>(null)
  // Only the LATEST tail may paint. Clicking Prod and then Dev inside the tail's
  // flight window used to let the older response land last and repaint the panel
  // with the other source's content under the newly-selected tab.
  const tailSeq = useRef(0)
  const tailAbort = useRef<AbortController | null>(null)
  // Whether the user is parked at the bottom (auto-scroll follows new output).
  // The ref drives the auto-scroll decision without re-running its effect; the
  // state drives the "Jump to latest" button's visibility.
  const atBottomRef = useRef(true)
  const [atBottom, setAtBottom] = useState(true)

  const scrollRef = useRef<HTMLPreElement | null>(null)
  const esRef = useRef<EventSource | null>(null)

  const setAtBottomState = useCallback((value: boolean) => {
    atBottomRef.current = value
    setAtBottom(value)
  }, [])

  // Auto-scroll to the newest line when the buffer grows — but only while the
  // user is parked at the bottom, so scrolling up to read isn't yanked back.
  useEffect(() => {
    const el = scrollRef.current
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight
  }, [lines])

  // When the user scrolls, recompute whether we're following the tail.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (el) setAtBottomState(isScrolledToBottom(el))
  }, [setAtBottomState])

  const jumpToLatest = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
    setAtBottomState(true)
  }, [setAtBottomState])

  // Prod is a single combined process, so the api/app split only applies to dev.
  const effectiveService: LogService = source === 'prod' ? 'all' : service

  // One-shot tail fetch for the current source + service. Only the most recent
  // call may write state — an in-flight one from a tab the user already left is
  // abandoned, not painted.
  const fetchTail = useCallback(async () => {
    const seq = ++tailSeq.current
    tailAbort.current?.abort()
    const controller = new AbortController()
    tailAbort.current = controller
    const isStale = (): boolean => seq !== tailSeq.current
    setLoading(true)
    try {
      const { path, params } = logsRequest(projectId, source, TAIL_LINES, effectiveService)
      // `silentErrors`: the panel shows its own banner right where the user is
      // looking, so the global toast would say the same thing twice.
      const res = await http.get<LogsResponse>(path, {
        params,
        signal: controller.signal,
        ...silentErrors(),
      })
      if (isStale()) return
      setLines(splitLogLines(res.data?.logs ?? ''))
      setContainerId(res.data?.containerId ?? null)
      setEmptyKey(null)
      setError(null)
      // A fresh tail resets us to the bottom (following the newest output).
      setAtBottomState(true)
    } catch (error) {
      if (isStale() || controller.signal.aborted) return
      setLines([])
      setContainerId(null)
      const empty = logsEmptyStateKey(error)
      if (empty) {
        // No dev sandbox / nothing deployed / asleep — an EXPECTED empty state,
        // not a failure. Clear the banner and let the empty-state copy speak (a
        // red "Failed to read logs" here was the IDE6 spurious banner).
        setEmptyKey(empty)
        setError(null)
      } else {
        console.error('LogsPanel: failed to fetch logs', error)
        setEmptyKey(null)
        setError(t('logs.loadError', undefined, { defaultValue: 'Failed to read logs.' }))
      }
    } finally {
      if (!isStale()) setLoading(false)
    }
  }, [http, projectId, source, effectiveService, t, setAtBottomState])

  // Re-tail when the source/service changes (and on mount). The stream effect
  // below re-establishes itself with the same key set.
  useEffect(() => {
    fetchTail()
  }, [fetchTail])

  // Abandon any in-flight tail when the panel closes.
  useEffect(
    () => () => {
      tailSeq.current += 1
      tailAbort.current?.abort()
    },
    [],
  )

  // Live SSE subscription — always on. Auth rides the httpOnly `token` cookie
  // via `withCredentials` (the server's verifyMiddleware falls back to it), so
  // we DON'T append the JWT as a `?token=` query param: that param was dead
  // (the SSE route never read it) and leaking a bearer token into a URL exposes
  // it to logs, history, and Referer headers. Re-subscribes when
  // source/service/project change; reconnects with backoff on drop and treats
  // the server's routine 10-min lifetime cap as a silent re-subscribe.
  useEffect(() => {
    let cancelled = false
    let es: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0

    const streamErrorMsg = t('logs.streamError', undefined, { defaultValue: 'Log stream error.' })

    const scheduleReconnect = (immediate = false): void => {
      if (cancelled || reconnectTimer) return
      if (attempt >= MAX_STREAM_ATTEMPTS) {
        // Stop. Retrying into a dependency that has failed this many times in a
        // row just adds load to it, and the user gets a Retry button instead of
        // an invisible loop.
        setError(streamErrorMsg)
        return
      }
      // Exponential backoff capped at 30s; the routine cap reconnects fast.
      const delay = immediate ? 500 : Math.min(30_000, 1000 * 2 ** attempt)
      attempt += 1
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        connect()
      }, delay)
    }

    const teardown = (current: EventSource): void => {
      current.close()
      if (esRef.current === current) esRef.current = null
    }

    const connect = (): void => {
      if (cancelled) return
      const url = `${API_BASE_URL}${logsStreamPath(projectId, source, TAIL_LINES, effectiveService)}`
      const next = new EventSource(url, { withCredentials: true })
      es = next
      esRef.current = next

      next.onopen = () => {
        // Connected — reset backoff and clear any prior soft stream error.
        attempt = 0
        setError((prev) => (prev === streamErrorMsg ? null : prev))
      }

      next.onmessage = (event) => {
        try {
          // Local widening of the shared union: the backend also emits
          // `{ type: 'empty', errorKey }` ONCE for expected-empty states (no
          // prod deployment / no sandbox yet) and keeps the stream open, so
          // logs start flowing if the environment appears later. The shared
          // `LogStreamEvent` in ops-utilities doesn't carry 'empty' yet.
          const data = JSON.parse(event.data as string) as
            LogStreamEvent | { type: 'empty'; errorKey?: string }
          if (data.type === 'logs') {
            setLines((prev) => appendLogDelta(prev, data.delta, DEFAULT_LOG_LINE_CAP))
            // Output is arriving, so whatever empty state we were in is over.
            setEmptyKey(null)
          } else if (data.type === 'reconnect') {
            // Routine lifetime cap — re-subscribe silently (no error banner).
            teardown(next)
            attempt = 0
            scheduleReconnect(true)
          } else if (data.type === 'empty') {
            // Expected empty state ("nothing running to show yet") — never a
            // failure. Clear any banner so the quiet empty-state copy renders.
            setEmptyKey(data.errorKey ?? null)
            setError(null)
          } else if (data.type === 'error') {
            // Belt-and-suspenders: an expected-empty errorKey riding a legacy
            // `error` frame is still not a failure — quiet empty state, not the
            // red "Log stream error." banner (the L-02 spurious banner).
            if (data.errorKey && EMPTY_STATE_ERROR_KEYS.has(data.errorKey)) {
              setEmptyKey(data.errorKey)
              setError(null)
            } else {
              // The server ENDS the stream after this frame. Closing our side is
              // what stops the browser from silently re-opening it on its own
              // timer — which it did, forever, with no backoff, because
              // `readyState` is CONNECTING (not CLOSED) on a server-side end, so
              // `onerror` below never ran and `attempt` never moved.
              setError(streamErrorMsg)
              teardown(next)
              scheduleReconnect()
            }
          }
        } catch (_error) {
          // ignore malformed events — SSE stream may emit non-JSON keep-alives or
          // partial frames; skipping them is safe, the next well-formed event continues normally
        }
      }

      next.onerror = () => {
        // The browser only surfaces a useful state via readyState. CLOSED means
        // it has GIVEN UP (won't auto-reconnect) — back off and re-create it
        // ourselves; CONNECTING means it's already auto-reconnecting (e.g. the
        // routine cap), so stay silent and let it recover. Only persist a banner
        // once reconnection has repeatedly failed, so a transient drop or the
        // 10-min cap never flashes an error.
        if (next.readyState === EventSource.CLOSED) {
          teardown(next)
          if (attempt >= 3) setError(streamErrorMsg)
          scheduleReconnect()
        }
      }
    }

    // Retry (the button in the error banner) re-subscribes in place: clear the
    // backoff, drop whatever handle we have, and connect again now.
    restartStreamRef.current = () => {
      if (cancelled) return
      attempt = 0
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      if (es) teardown(es)
      setError(null)
      connect()
    }

    connect()

    return () => {
      cancelled = true
      restartStreamRef.current = null
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (es) {
        es.close()
        if (esRef.current === es) esRef.current = null
      }
    }
  }, [source, effectiveService, projectId, t])

  // Retry both halves: a fresh tail (which repaints the buffer) and a fresh
  // stream subscription.
  const handleRetry = useCallback(() => {
    setError(null)
    restartStreamRef.current?.()
    void fetchTail()
  }, [fetchTail])

  const handleClear = useCallback(() => setLines([]), [])

  // Pre-classify each line into its theme color ONCE per buffer change (a single
  // component-level memo — no per-line hooks), so a scroll-only re-render never
  // re-runs the classifier over the up-to-2000-line cap.
  const coloredLines = useMemo(
    () => lines.map((text) => ({ text, color: LOG_LEVEL_COLOR[classifyLogLine(text).level] })),
    [lines],
  )

  const hasContainer = !!containerId
  const isEmpty = lines.length === 0

  /**
   * What the quiet empty state SAYS. One sentence, naming the actual situation:
   * asleep, never deployed, and gone are three different things, and the panel
   * used to answer all of them with whichever one matched the tab.
   */
  const emptyStateCopy =
    emptyKey === 'projectOps.error.runtimeAsleep'
      ? source === 'dev'
        ? t('logs.empty.devAsleep', undefined, {
            defaultValue: 'The dev sandbox is asleep. Wake it to see logs.',
          })
        : t('logs.empty.prodAsleep', undefined, {
            defaultValue: 'The production deployment is not running right now.',
          })
      : source === 'dev'
        ? t('logs.empty.dev', undefined, {
            defaultValue: 'No dev container running. Start the sandbox to see logs.',
          })
        : t('logs.empty.prod', undefined, {
            defaultValue: 'No production deployment is running yet.',
          })

  /**
   * dev / prod source toggle + (dev) api/app/all service selector — shared
   * style. The active state is driven entirely by the `--color-primary` theme
   * token (border/tint/text), so it tracks the user's theme and never paints a
   * hardcoded brand-blue hex (the IDE6 ClassMap-rule violation). Matches the
   * Services/Activity segmented-toggle treatment for one consistent IDE look.
   */
  const segButtonStyle = (active: boolean): React.CSSProperties => ({
    height: controlHeight,
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    padding: isMobile ? '0 14px' : '0 10px',
    // A pill label never wraps — the row wraps whole pills.
    whiteSpace: 'nowrap',
    borderRadius: 6,
    cursor: 'pointer',
    border: active
      ? '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)'
      : '1px solid var(--color-border-secondary)',
    background: active
      ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)'
      : 'transparent',
    color: active ? 'var(--color-primary)' : 'inherit',
    transition: 'background 100ms, border-color 100ms',
  })

  return (
    // Fill the modal: a flex column whose log viewport grows to take all the
    // height the PanelModal body offers (capped only by the modal's own ~70vh),
    // instead of the old hard 320px box that left most of the modal empty (IDE6).
    //
    // `flex: 1` + `minHeight: 0`, NOT `height: '100%'` — the modal body's height
    // comes from flex clamping, which percentages do not resolve against, so
    // `100%` grew the panel to its full content (a 10,000px `<pre>` with nothing
    // to scroll: auto-scroll was a no-op and "Jump to latest" could never
    // appear). Requires the modal's `fillBody` (see PanelModal.css).
    <div
      style={{
        padding: 16,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
      data-mol-id="logs-panel"
    >
      {/* The "Logs" title lives in the modal chrome and the Dev/Prod source
          tabs in the modal header (usePanelModalTabs above) — this row keeps
          only the in-view controls. */}
      <Flex align="center" gap="sm" style={{ marginBottom: 12 }} wrap="wrap">
        {/* service selector — dev only (prod is one combined log) */}
        {source === 'dev' && (
          <Flex align="center" gap="xs" data-mol-id="logs-service-toggle">
            {(['all', 'api', 'app'] as const).map((svc) => (
              <button
                key={svc}
                type="button"
                data-mol-id={`logs-service-${svc}`}
                aria-pressed={service === svc}
                onClick={() => setService(svc)}
                style={segButtonStyle(service === svc)}
              >
                {svc === 'all'
                  ? t('logs.service.all', undefined, { defaultValue: 'All' })
                  : svc === 'api'
                    ? t('logs.service.api', undefined, { defaultValue: 'API' })
                    : t('logs.service.app', undefined, { defaultValue: 'App' })}
              </button>
            ))}
          </Flex>
        )}

        <Button
          size="sm"
          variant="outline"
          onClick={handleClear}
          data-mol-id="logs-clear"
          style={{ height: controlHeight, marginLeft: 'auto' }}
        >
          {t('logs.clear', undefined, { defaultValue: 'Clear' })}
        </Button>
      </Flex>

      {error && (
        <Flex
          align="center"
          justify="between"
          gap="sm"
          wrap="wrap"
          data-mol-id="logs-error"
          className={cm.textError}
          style={{ padding: 10, fontSize: 13, marginBottom: 10 }}
        >
          <span>{error}</span>
          {/* A failed stream stops retrying itself, so the way back has to be
              reachable — otherwise the only recovery is closing the panel. */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRetry}
            data-mol-id="logs-retry"
            style={{ height: controlHeight }}
          >
            {t('logs.retry', undefined, { defaultValue: 'Retry' })}
          </Button>
        </Flex>
      )}

      {loading ? (
        <Flex align="center" justify="center" className={cm.sp('py', 6)}>
          <MoleculeSpinner />
        </Flex>
      ) : !hasContainer && isEmpty && !error ? (
        <div
          data-mol-id="logs-empty"
          style={{
            textAlign: 'center',
            padding: 20,
            fontSize: 13,
            color: 'var(--color-foreground-tertiary)',
          }}
        >
          {emptyStateCopy}
        </div>
      ) : (
        // Grow to fill the remaining modal height; the <pre> scrolls internally.
        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
          <pre
            ref={scrollRef}
            onScroll={handleScroll}
            data-mol-id="logs-output"
            style={{
              flex: 1,
              minHeight: 0,
              margin: 0,
              padding: 12,
              overflow: 'auto',
              fontFamily: 'var(--mol-font-mono, monospace)',
              fontSize: 12,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              color: 'var(--color-foreground-secondary)',
            }}
          >
            {isEmpty
              ? t('logs.noLines', undefined, { defaultValue: 'No log output.' })
              : coloredLines.map((ln, i) => (
                  // One lightweight node per line (no per-line hooks), level-colored
                  // via a theme token. whiteSpace/wordBreak/font are inherited from
                  // the <pre>; an interior blank line renders a preserved space (the
                  // <pre> is pre-wrap) so its row keeps a full line of height.
                  <div key={i} data-mol-id="logs-line" style={{ color: ln.color }}>
                    {ln.text === '' ? ' ' : ln.text}
                  </div>
                ))}
          </pre>

          {/* Auto-scroll is paused — offer to jump back to the live tail. The
              floating pill is the shared themed Button (primary surface + text
              from theme tokens, never a hardcoded brand-blue/white hex) with the
              SVG arrow-down glyph from the icon set. */}
          {!atBottom && (
            <Button
              color="primary"
              variant="solid"
              size="sm"
              data-mol-id="logs-jump-latest"
              onClick={jumpToLatest}
              leftIcon={<Icon name="arrow-down" size={14} aria-hidden="true" />}
              style={{
                position: 'absolute',
                right: 12,
                bottom: 12,
                borderRadius: 999,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              {t('logs.jumpToLatest', undefined, { defaultValue: 'Jump to latest' })}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
